import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { Language } from '../shared/models/app.model';
import { memoize } from '@app/shared/memoize';
import { SUPPORTED_LANGUAGES } from './supported-languages.const';

export interface LanguageSelection {
  transcriptionLanguage: Language;
  translationLanguage: Language;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  public currentLanguage$: Observable<LanguageSelection>;
  public languages: Language[];

  private readonly defaultLang = 'en-US';
  private currentLanguageSubject: BehaviorSubject<LanguageSelection>;

  constructor() {
    this.languages = SUPPORTED_LANGUAGES;

    const storedLanguage = localStorage.getItem('currentLanguage');
    let transcriptionLangCode = this.defaultLang;
    let translationLangCode = this.defaultLang;

    if (storedLanguage) {
      try {
        // Try to parse as JSON (new format)
        const parsed = JSON.parse(storedLanguage);
        if (parsed.transcriptionLanguage && parsed.translationLanguage) {
          transcriptionLangCode = parsed.transcriptionLanguage;
          translationLangCode = parsed.translationLanguage;
        } else {
          // Invalid object format, use default
          transcriptionLangCode = this.defaultLang;
          translationLangCode = this.defaultLang;
        }
      } catch (e) {
        // Old format (plain string), migrate it
        transcriptionLangCode = storedLanguage;
        translationLangCode = storedLanguage;
      }
    }

    this.currentLanguageSubject = new BehaviorSubject({
      // if stored language is not valid, use default
      transcriptionLanguage: this.getLangFromCode(transcriptionLangCode) ?? this.getLangFromCode(this.defaultLang),
      translationLanguage: this.getLangFromCode(translationLangCode) ?? this.getLangFromCode(this.defaultLang),
    });

    this.currentLanguage$ = this.currentLanguageSubject.asObservable();
    this.currentLanguage$.subscribe((x) => {
      const languageData = {
        transcriptionLanguage: x.transcriptionLanguage.languageCode,
        translationLanguage: x.translationLanguage.languageCode
      };
      localStorage.setItem('currentLanguage', JSON.stringify(languageData));
    });
  }

  public setTranscriptionLanguage(languageCode: string): void {
    const lang = this.getLangFromCode(languageCode);
    if (!lang) {
      console.error(`Language with code ${languageCode} does not exist`);
      return;
    }
    this.currentLanguageSubject.next({
      ...this.currentLanguageSubject.value,
      ...({
        transcriptionLanguage: lang,
      } as LanguageSelection),
    });
  }

  public setTranslationLanguage(languageCode: string): void {
    const lang = this.getLangFromCode(languageCode);
    if (!lang) {
      console.error(`Language with code ${languageCode} does not exist`);
      return;
    }
    this.currentLanguageSubject.next({
      ...this.currentLanguageSubject.value,
      ...({
        translationLanguage: lang,
      } as LanguageSelection),
    });
  }

  @memoize()
  public getLangFromCode(code: string): Language {
    return this.languages.find((lang) => lang.languageCode === code);
  }

  @memoize()
  public getLanguageCode(language: Language): string {
    return language
      ? language?.languageCode?.substring(0, 2).toUpperCase()
      : 'error';
  }
}
